<template lang="pug">
.progress-overview
  .header
    span.label 今日领取进度
    span.value {{ receivedAmount }}/{{ totalAmount }}

  .ladder
    .ladder-icons
      .icon-item(v-for="(item, index) in subTasks" :key="'icon-' + index")
        img(:src="item.coinIcon")

    .ladder-coins
      template(v-for="(item, index) in subTasks")
        .coin(
          :key="'coin-' + index"
          :class="item.coinClass"
        ) {{ item.coinText }}
        .track(
          v-if="index < subTasks.length - 1"
          :key="'track-' + index"
          :class="'track-' + item.trackState"
        )

    .ladder-labels
      .label-item(
        v-for="(item, index) in subTasks"
        :key="'label-' + index"
        :class="item.labelClass"
      )
        span.label-title {{ item.title }}
        span.label-time {{ item.timeText }}

  .remind(@click="handleToggleRemind")
    span.remind-label 领取提醒
    span.remind-switch(:class="{ 'is-on': remindSubscribed }")
</template>

<script>
import dayjs from 'dayjs';
import { getTokenAndKey } from '@/utils';
import { setSubsidyNotice } from '@/api/bounty';
import { COIN_ICON } from '../common/constants';
import { isInPeriod, isExpired } from '../common/utils';
import { subscribeRemind, unsubscribeRemind } from '../common/calendar-remind';


export default {
  name: 'ProgressOverview',
  props: {
    taskGroup: { type: Object, default: () => ({}) },
  },
  data() {
    return { remindSubscribed: false };
  },
  computed: {
    subTasks() {
      const list = this.taskGroup.subsidyDetailInfos || [];
      return list.map((item, index) => {
        const expired = isExpired(item);
        const active = isInPeriod(item);
        const received = Number(item.drawTaskBountyCount) || 0;
        const total = Number(item.taskBountyCount) || 0;
        const noReceive = !received;

        let title = item.taskName;
        if (expired) {
          title = received
            ? `已领·${item.taskName}`
            : `未领·${item.taskName}`;
        }

        let coinText = `+${total}`;
        if (active) coinText = `${received}/${total}`;
        else if (expired && received) coinText = `+${received}`;

        const fmt = (t) => {
          if (t && t.startsWith('24')) return `24:${t.slice(3, 5)}`;
          return dayjs(`2000-01-01 ${t}`).format('HH:mm');
        };

        // 连接线状态（当前 item 到下一个 item 之间）
        let trackState = '';
        const next = list[index + 1];
        if (next) {
          const nextExpired = isExpired(next);
          const nextActive = isInPeriod(next);
          if (expired && (nextExpired || nextActive)) trackState = 'full';
          else if (expired) trackState = 'half';
          else trackState = 'gray';
        }

        return {
          ...item,
          // eslint-disable-next-line no-nested-ternary
          coinClass: expired
            ? (noReceive ? 'is-expired' : 'is-received') : '',
          coinIcon: (expired && noReceive)
            ? COIN_ICON.GRAY : COIN_ICON.GOLD,
          // eslint-disable-next-line no-nested-ternary
          labelClass: expired
            ? 'is-past' : (active ? 'is-current' : ''),
          title,
          coinText,
          timeText: `${fmt(item.startTime)}～${fmt(item.endTime)}`,
          trackState,
        };
      });
    },
    earliestStartTime() {
      return (this.subTasks[0] || {}).startTime || '';
    },
    receivedAmount() {
      return Number(this.taskGroup?.drawTaskBountyCountSum) || 0;
    },
    totalAmount() {
      return Number(this.taskGroup?.taskBountyCountSum) || 0;
    },
  },
  watch: {
    'taskGroup.isOpenNotice': {
      handler(val) {
        this.remindSubscribed = !!val;
      },
      immediate: true,
    },
  },
  methods: {
    async handleToggleRemind() {
      const willSubscribe = !this.remindSubscribed;
      const ok = willSubscribe
        ? await subscribeRemind(this.earliestStartTime)
        : await unsubscribeRemind();
      if (ok) {
        this.remindSubscribed = willSubscribe;
        this.syncNoticeStatus(willSubscribe);
      }
    },
    async syncNoticeStatus(isOpenNotice) {
      try {
        const { token } = await getTokenAndKey();
        await setSubsidyNotice({ token, isOpenNotice });
      } catch (e) {
        // 接口失败不影响本地状态，静默处理
      }
    },
  },
};
</script>

<style lang="less" scoped>
.progress-overview {
  width: 345px;
  position: absolute;
  top: 172px;
  left: 0;
  right: 0;
  margin: 0 auto;
  padding: 12px 0;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  border: 1px solid transparent;
  &::before {
    content: "";
    position: absolute;
    inset: -1px;
    padding: 1px;
    border-radius: inherit;
    background: radial-gradient(rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0) 34.13%, rgba(255, 255, 255, 0.6) 68.27%, rgba(255, 255, 255, 0) 100%);
    pointer-events: none;
    mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    mask-composite: exclude;
  }
  .header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 16px;
    padding: 0 12px;

    .label,
    .value {
      font-size: 16px;
      font-weight: 600;
      color: #fff;
    }
  }

  .ladder {
    padding: 0 12px;

    .ladder-icons {
      display: flex;
      justify-content: space-between;
      margin-bottom: 6px;

      .icon-item {
        width: 76px;
        display: flex;
        justify-content: center;

        img {
          width: 25px;
          height: 25px;
        }
      }
    }

    .ladder-coins {
      display: flex;
      align-items: center;

      .coin {
        width: 76px;
        height: 25px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(180deg, #fff9e6 0%, #ffe188 100%);
        border-radius: 63px;
        flex-shrink: 0;
        position: relative;
        z-index: 1;
        font-size: 15px;
        font-weight: 600;
        color: rgba(0, 0, 0);

        &.is-received {
          background: linear-gradient(180deg, #ffe9a8 0%, #dcbd5f 100%);
        }

        &.is-expired {
          color: rgba(0, 0, 0, 0.5);
          background: linear-gradient(180deg, #efefef 0%, #d4d3d3 100%);
        }
      }

      .track {
        flex: 1;
        height: 8px;
        margin: 0 -4px;
        background: rgba(255, 255, 255, 0.4);

        &.track-full {
          background: #ffc04b;
        }

        &.track-half {
          overflow: hidden;
          position: relative;

          &::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 50%;
            height: 100%;
            background: #ffc04b;
            border-radius: 0 4px 4px 0;
          }
        }
      }
    }

    .ladder-labels {
      display: flex;
      justify-content: space-between;
      margin-top: 5px;

      .label-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 76px;
        color: #fff;

        &.is-past {
          color: rgba(255, 255, 255, 0.6);
        }

        &.is-current {
          color: #ffeeba;
        }

        .label-title {
          font-size: 14px;
          font-weight: 600;
          max-width: 100px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          text-align: center;
        }

        .label-time {
          font-size: 12px;
          font-weight: 400;
          white-space: nowrap;
        }
      }
    }
  }

  .remind {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 0 0 18px 18px;
    width: 148px;
    height: 55px;
    background: url('https://m.hellobike.com/88f64c00_Union%20(2)%20(1).png') top right / contain no-repeat;

    .remind-label {
      font-size: 15px;
      font-weight: 600;
      color: #452b1a;
    }

    .remind-switch {
      width: 32px;
      height: 16px;
      border-radius: 8px;
      background: #ababab;
      position: relative;
      transition: background 0.2s;

      &::after {
        content: '';
        position: absolute;
        top: 1px;
        left: 1px;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: #fff;
        transition: transform 0.2s;
      }

      &.is-on {
        background: #ffd244;

        &::after {
          transform: translateX(16px);
        }
      }
    }
  }
}
</style>
